import { useEffect, useRef } from 'react';
import { useCurrentTheme } from '../hooks/useCurrentTheme';
import { useMatchMedia } from '../hooks/useMatchMedia';
import { OS_DESKTOP_MEDIA_QUERY } from './osRouteMeta';
import type { WaterScene } from './waterScene';
import { subscribeWaterRipple } from './waterRipple';
import { useIsMcGrawHill } from '../mcgrawHill/mcgrawHill'; // mcgraw-hill
import { MCGRAW_HILL_LIGHT_WATER_GRADIENT } from '../mcgrawHill/mcgrawHillTheme'; // mcgraw-hill
import '../mcgrawHill/mcgrawHillTheme.css'; // mcgraw-hill

interface OsWaterBackgroundProps {
  isActive: boolean;
}

export default function OsWaterBackground({
  isActive,
}: OsWaterBackgroundProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<WaterScene | null>(null);
  const { isDarkMode } = useCurrentTheme();
  // Below the OS breakpoint there is no desktop: windows run full-bleed and
  // / forwards to the Dashboard, so nothing would ever show through. Phones
  // skip the backdrop, the three.js chunk and the WebGL context entirely.
  const isDesktopViewport = useMatchMedia(OS_DESKTOP_MEDIA_QUERY);

  // mcgraw-hill: the light water takes McGraw Hill's steel blue.
  const lightGradient = useIsMcGrawHill()
    ? MCGRAW_HILL_LIGHT_WATER_GRADIENT
    : undefined;

  const settingsRef = useRef({ isActive, isDarkMode, lightGradient });

  useEffect(() => {
    settingsRef.current = { isActive, isDarkMode, lightGradient };
    sceneRef.current?.update(settingsRef.current);
  }, [isActive, isDarkMode, lightGradient]);

  // Ripples asked for elsewhere (the knowledge map opening) go to the live
  // scene; before it has loaded there is no water to disturb.
  useEffect(
    () => subscribeWaterRipple((point) => sceneRef.current?.ripple(point)),
    []
  );

  useEffect(() => {
    if (!isDesktopViewport) return;

    let isDisposed = false;
    const host = hostRef.current;
    if (!host) return;

    // The image backdrop is already painted while the optional WebGL loads.
    void import('./waterScene')
      .then(({ createWaterScene }) => {
        if (isDisposed) return;
        sceneRef.current = createWaterScene(host, settingsRef.current);
      })
      .catch(() => {
        // Keep the static atmosphere on devices without usable WebGL.
      });

    return () => {
      isDisposed = true;
      sceneRef.current?.dispose();
      sceneRef.current = null;
    };
  }, [isDesktopViewport]);

  if (!isDesktopViewport) return null;

  return <div aria-hidden="true" className="os-water" ref={hostRef} />;
}
